import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DetailPageLayout } from '../../../components/layouts';
import { usePageTitle } from '../../../hooks/shared';
import { authoritiesAPI } from '../../authorities/api/authorities';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_found: 'bg-orange-100 text-orange-700',
  unknown: 'bg-neutral-100 text-neutral-500',
};

function SuggestForm({ authorityId, onSuccess }) {
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL (include https://)');
      return;
    }
    setSaving(true);
    try {
      await authoritiesAPI.submitUrl(authorityId, url, notes || undefined);
      onSuccess();
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">
          Official election website URL
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError('');
          }}
          placeholder="https://vote.countyname.gov/"
          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-700 mb-1">Notes (optional)</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. County clerk's election page"
          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={saving || !url}
        className="w-full py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        {saving ? 'Submitting…' : 'Submit suggestion'}
      </button>
    </form>
  );
}

function SuggestWidget({ authority, onSuccess }) {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="flex items-center gap-2 text-emerald-700">
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm font-medium">
          Thanks! Your suggestion has been submitted for review.
        </span>
      </div>
    );
  }

  if (showForm) {
    return (
      <>
        <h3 className="font-semibold text-neutral-900 mb-3">
          {authority.websiteUrl ? 'Suggest a correction' : 'Add the official website'}
        </h3>
        <SuggestForm
          authorityId={authority.id}
          onSuccess={() => {
            setShowForm(false);
            setSubmitted(true);
          }}
        />
        <button
          onClick={() => setShowForm(false)}
          className="mt-3 w-full text-xs text-neutral-400 hover:text-neutral-500"
        >
          Cancel
        </button>
      </>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-neutral-900">
          {authority.websiteUrl ? 'Know a better URL?' : 'Know the official website?'}
        </p>
        <p className="text-xs text-neutral-500 mt-0.5">
          Suggestions go to our admins for review before going live.
        </p>
      </div>
      <button
        onClick={() => setShowForm(true)}
        className="shrink-0 ml-4 px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
      >
        {authority.websiteUrl ? 'Suggest correction' : 'Add website'}
      </button>
    </div>
  );
}

export default function CountyDetail() {
  const { state, county } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['authorities', 'by-location', state, county],
    queryFn: () =>
      authoritiesAPI.list({
        state: state.toUpperCase(),
        q: decodeURIComponent(county),
        limit: 5,
      }),
  });

  const authority = data?.authorities?.[0];
  const checks = authority?.websiteChecks ?? [];

  usePageTitle(authority ? `${authority.name} — Election Website` : 'Election Authority');

  return (
    <DetailPageLayout
      title={authority?.name}
      breadcrumbs={[{ label: 'Directory', url: '/counties' }, { label: authority?.name }]}
      isLoading={isLoading}
      error={error}
      hero={
        authority && (
          <div>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-sm text-neutral-500 mb-2">
                  {authority.state}
                  {authority.county ? ` · ${authority.county} County` : ''}
                </p>
              </div>
              <span
                className={`shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full ${STATUS_STYLES[authority.websiteStatus] ?? 'bg-neutral-100 text-neutral-500'}`}
              >
                {authority.websiteStatus}
              </span>
            </div>

            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                Official election website
              </p>
              {authority.websiteUrl ? (
                <a
                  href={authority.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 font-medium break-all text-sm"
                >
                  {authority.websiteUrl}
                </a>
              ) : (
                <p className="text-neutral-400 italic text-sm">No website on file</p>
              )}
            </div>

            {authority.lastCheckedAt && (
              <p className="text-xs text-neutral-400 mt-3">
                Last verified{' '}
                {new Date(authority.lastCheckedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}
          </div>
        )
      }
      sections={[
        {
          title: authority?.websiteUrl ? 'Suggest a correction' : 'Add the official website',
          content: authority && <SuggestWidget authority={authority} onSuccess={() => {}} />,
        },
        ...(checks.length > 0
          ? [
              {
                title: 'Recent website checks',
                content: (
                  <div className="space-y-2">
                    {checks.slice(0, 10).map((check) => (
                      <div
                        key={check.id}
                        className="flex items-center justify-between text-xs text-neutral-500"
                      >
                        <span>{new Date(check.checkedAt).toLocaleString()}</span>
                        <div className="flex items-center gap-3">
                          {check.httpStatus && (
                            <span
                              className={
                                check.httpStatus < 400 ? 'text-emerald-600' : 'text-red-500'
                              }
                            >
                              HTTP {check.httpStatus}
                            </span>
                          )}
                          {check.changeDetected && (
                            <span className="text-amber-600 font-medium">Changed</span>
                          )}
                          {check.errorMessage && (
                            <span className="text-red-500 truncate max-w-32">
                              {check.errorMessage}
                            </span>
                          )}
                          {check.responseTimeMs && <span>{check.responseTimeMs}ms</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}
